import React from 'react';
import { Badge } from '@roger/ui-components';

interface ClickableTextWithModalsProps {
  text: string;
  onOpenModal?: (dataType: string) => void;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

/**
 * Component that parses text and converts @-prefixed words into clickable badges
 * that can trigger modal displays.
 */
const ClickableTextWithModals: React.FC<ClickableTextWithModalsProps> = ({
  text,
  onOpenModal,
  className = '',
  size = 'md',
}) => {
  if (!text) return null;

  // Parse the text to identify @tags and handle newlines
  const parseText = (inputText: string) => {
    // Split by newlines to handle line breaks
    const lines = inputText.split(/\n/);

    return lines.map((line, lineIndex) => {
      // Split each line by @tags while preserving the tags
      // Use negative lookbehind to avoid matching email addresses (e.g., user@example.com)
      const parts = line.split(/(?<![\w.-])(@\w+(?:[_]\w+)*)/g);

      const lineContent = parts.map((part, index) => {
        if (part.startsWith('@')) {
          console.log('part', part);

          // Extract tag name without @
          const tagName = part.substring(1);

          // Process the display text: split by underscores, replace with spaces, capitalize each word
          const displayText = tagName
            .split('_')
            .map(
              word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(' ');

          const sizeClasses = {
            xs: 'text-[0.65rem] px-1.5 py-0.5',
            sm: 'text-xs px-2 py-0.5',
            md: 'text-sm px-2 py-1',
            lg: 'text-base px-3 py-1',
          };

          return (
            <Badge
              key={`${lineIndex}-${index}`}
              variant='secondary'
              className={`cursor-pointer mx-1 font-semibold border border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100 hover:border-primary-500 transition-all hover:-translate-y-0.5 hover:shadow-md ${sizeClasses[size]}`}
              onClick={() => onOpenModal?.(tagName)}
            >
              {displayText}
            </Badge>
          );
        } else {
          // Regular text
          return <span key={`${lineIndex}-${index}`}>{part}</span>;
        }
      });

      // Return each line as a div, with line breaks between them
      return (
        <div key={lineIndex} className={size === 'xs' ? 'mb-1' : 'mb-2'}>
          {lineContent}
        </div>
      );
    });
  };

  const parsedContent = parseText(text);

  const sizeClasses = {
    xs: 'text-xs leading-relaxed',
    sm: 'text-sm leading-relaxed',
    md: 'text-base leading-relaxed',
    lg: 'text-lg leading-relaxed',
  };

  return (
    <div className={`text-gray-700 ${sizeClasses[size]} ${className}`}>
      {parsedContent}
    </div>
  );
};

export default ClickableTextWithModals;
